import React, { useMemo, useContext } from "react";
import { Modal, Drawer, Button, DatePicker } from "antd";
import HeaderDropDown from "../Header-dropdown/HeaderDropDown";
import { AppContext } from "../../../context/AppContext";
import moment from "moment";
import "./styles.css";

const FilterModal = ({
  isOpen,
  onClose,
  enforceSelection = false,
  isDesktop,
  isMobile,
  isTablet,
}) => {
  const {
    projects,
    selectedProject,
    setActiveProject,
    selectedRegionId,
    setSelectedRegionId,
    selectedCouncilId,
    setSelectedCouncilId,
    selectedReportID,
    setSelectedReportID,
    selectedCscDateRange,
    setSelectedCscDateRange,
    regionOptions,
    councilOptions,
    cscReportOptions,
    setSelectedReportDetails,
    refetchSelectedReportDetails,
    isRegionOptionsLoading,
    isCouncilOptionsLoading,
    // eslint-disable-next-line
  } = useContext(AppContext);

  const regions = regionOptions;
  const councils = councilOptions;
  const reports = cscReportOptions;

  const projectsOptions = useMemo(() => {
    return (projects || []).map((p) => ({ value: p.id, label: p.name }));
  }, [projects]);

  const canApply = !!selectedReportID;
  const dateRangeValue = useMemo(() => {
    if (!Array.isArray(selectedCscDateRange)) return null;
    const [start, end] = selectedCscDateRange;
    if (start && end) {
      return [moment(start), moment(end)];
    }
    return null;
  }, [selectedCscDateRange]);

  return (
    <>
      {(isDesktop || isTablet) && (
        <Modal
          title="Filters"
          open={isOpen}
          onCancel={onClose}
          data-testid="filter-modal"
          footer={[
            !enforceSelection && (
              <Button key="cancel" onClick={onClose}>
                Cancel
              </Button>
            ),
            <Button
              key="apply"
              type="primary"
              data-testid="filter-apply-button"
              onClick={() => {
                if (setSelectedReportDetails) setSelectedReportDetails(null);
                if (typeof refetchSelectedReportDetails === "function") {
                  refetchSelectedReportDetails();
                }
                onClose();
              }}
              disabled={!canApply}
            >
              Apply
            </Button>,
          ].filter(Boolean)}
          getContainer={() => document.body}
          // centered
          style={{ top: 70 }}
          className="filter-modal-desktop-tablet"
          maskClosable={true}
          keyboard={true}
          closable={true}
          width={840}
          destroyOnClose
        >
          {enforceSelection && (
            <div
              style={{
                marginBottom: 12,
                padding: "8px 12px",
                background: "#fffbe6",
                border: "1px solid #ffe58f",
                borderRadius: 4,
                color: "#8c6d1f",
              }}
            >
              Please select a valid report to continue.
            </div>
          )}
          <div className="filter-modal__form">
            <div className="filter-modal__field">
              <div className="filter-modal__label">Project *</div>
              <HeaderDropDown
                options={projectsOptions}
                onChange={(val) => setActiveProject(val)}
                placeholder="Project"
                value={selectedProject ?? undefined}
                testId="filter-project-select"
                classNamePrefix="select-dropdown project"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth={true}
                disabled={projectsOptions.length === 0}
              />
            </div>
            <div className="filter-modal__field">
              <div className="filter-modal__label">Region *</div>
              <HeaderDropDown
                options={regions}
                onChange={(val) => {
                  setSelectedRegionId(val);
                  setSelectedCouncilId(null);
                  setSelectedCscDateRange([null, null]);
                  setSelectedReportID(null);
                  if (setSelectedReportDetails) setSelectedReportDetails(null);
                }}
                placeholder="Region"
                value={selectedRegionId ?? undefined}
                testId="filter-region-select"
                classNamePrefix="select-dropdown region"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth={true}
                disabled={regions.length === 0}
                loading={isRegionOptionsLoading}
              />
            </div>
            <div className="filter-modal__field">
              <div className="filter-modal__label">LGA *</div>
              <HeaderDropDown
                options={councils}
                onChange={(val) => {
                  setSelectedCouncilId(val);
                  setSelectedCscDateRange([null, null]);
                  setSelectedReportID(null);
                  if (setSelectedReportDetails) setSelectedReportDetails(null);
                }}
                placeholder="LGA"
                value={selectedCouncilId ?? undefined}
                testId="filter-council-select"
                classNamePrefix="select-dropdown municipal"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth={true}
                disabled={councils.length === 0}
                loading={isCouncilOptionsLoading}
              />
            </div>
            <div className="filter-modal__field">
              <div className="filter-modal__label">Date Range (optional)</div>
              <DatePicker.RangePicker
                value={dateRangeValue}
                onChange={(vals) => {
                  if (Array.isArray(vals) && vals[0] && vals[1]) {
                    setSelectedCscDateRange([
                      vals[0].format("YYYY-MM-DD"),
                      vals[1].format("YYYY-MM-DD"),
                    ]);
                  } else {
                    setSelectedCscDateRange([null, null]);
                  }
                  setSelectedReportID(null);
                  if (setSelectedReportDetails) setSelectedReportDetails(null);
                }}
                allowEmpty={[true, true]}
                placeholder={["Start date", "End date"]}
                className="select-dropdown date-range"
                getPopupContainer={() => document.body}
                disabled={!selectedCouncilId}
              />
            </div>
            <div className="filter-modal__field filter-modal__field--full">
              <div className="filter-modal__label">CSC Report *</div>
              <HeaderDropDown
                options={reports}
                onChange={setSelectedReportID}
                placeholder="Report#"
                value={selectedReportID ?? undefined}
                testId="filter-report-select"
                classNamePrefix="select-dropdown report"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth={true}
                disabled={!selectedProject || !selectedRegionId || !selectedCouncilId || (!reports || reports.length === 0)}
              />
            </div>
          </div>
        </Modal>
      )}

      {isMobile && (
        <Drawer
          title="Filters"
          placement="bottom"
          height="80%" // adjust as needed (px or %)
          className="filter-modal-mobile-tablet"
          open={isOpen}
          onClose={onClose}
          data-testid="filter-modal"
          closable={true}
          destroyOnClose
          maskClosable
          keyboard
          getContainer={() => document.body}
          footer={
            <div style={{ textAlign: "right" }}>
              {!enforceSelection && (
                <Button
                  key="cancel"
                  onClick={onClose}
                  style={{ marginRight: 8 }}
                >
                  Cancel
                </Button>
              )}
              <Button
                key="apply"
                type="primary"
                data-testid="filter-apply-button"
                onClick={() => {
                  if (setSelectedReportDetails) setSelectedReportDetails(null);
                  if (typeof refetchSelectedReportDetails === "function") {
                    refetchSelectedReportDetails();
                  }
                  onClose();
                }}
                disabled={!canApply}
              >
                Apply
              </Button>
            </div>
          }
        >
          {enforceSelection && (
            <div
              style={{
                marginBottom: 12,
                padding: "8px 12px",
                background: "#fffbe6",
                border: "1px solid #ffe58f",
                borderRadius: 4,
                color: "#8c6d1f",
              }}
            >
              Please select a valid report to continue.
            </div>
          )}

          <div className="filter-modal__form">
            <div className="filter-modal__field">
              <div className="filter-modal__label">Project *</div>
              <HeaderDropDown
                options={projectsOptions}
                onChange={(val) => setActiveProject(val)}
                placeholder="Project"
                value={selectedProject ?? undefined}
                testId="filter-project-select"
                classNamePrefix="select-dropdown project"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth
                disabled={projectsOptions.length === 0}
              />
            </div>

            <div className="filter-modal__field">
              <div className="filter-modal__label">Region *</div>
              <HeaderDropDown
                options={regions}
                onChange={(val) => {
                  setSelectedRegionId(val);
                  setSelectedCouncilId(null);
                  setSelectedCscDateRange([null, null]);
                  setSelectedReportID(null);
                  if (setSelectedReportDetails) setSelectedReportDetails(null);
                }}
                placeholder="Region"
                value={selectedRegionId ?? undefined}
                testId="filter-region-select"
                classNamePrefix="select-dropdown region"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth
                disabled={regions.length === 0}
                loading={isRegionOptionsLoading}
              />
            </div>

            <div className="filter-modal__field">
              <div className="filter-modal__label">LGA *</div>
              <HeaderDropDown
                options={councils}
                onChange={(val) => {
                  setSelectedCouncilId(val);
                  setSelectedCscDateRange([null, null]);
                  setSelectedReportID(null);
                  if (setSelectedReportDetails) setSelectedReportDetails(null);
                }}
                placeholder="LGA"
                value={selectedCouncilId ?? undefined}
                testId="filter-council-select"
                classNamePrefix="select-dropdown municipal"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth
                disabled={councils.length === 0}
                loading={isCouncilOptionsLoading}
              />
            </div>

            <div className="filter-modal__field">
              <div className="filter-modal__label">Date Range (optional)</div>
              <DatePicker.RangePicker
                value={dateRangeValue}
                onChange={(vals) => {
                  if (Array.isArray(vals) && vals[0] && vals[1]) {
                    setSelectedCscDateRange([
                      vals[0].format("YYYY-MM-DD"),
                      vals[1].format("YYYY-MM-DD"),
                    ]);
                  } else {
                    setSelectedCscDateRange([null, null]);
                  }
                  setSelectedReportID(null);
                  if (setSelectedReportDetails) setSelectedReportDetails(null);
                }}
                allowEmpty={[true, true]}
                placeholder={["Start date", "End date"]}
                className="select-dropdown date-range"
                getPopupContainer={() => document.body}
                disabled={!selectedCouncilId}
              />
            </div>

            <div className="filter-modal__field filter-modal__field--full">
              <div className="filter-modal__label">CSC Report *</div>
              <HeaderDropDown
                options={reports}
                onChange={setSelectedReportID}
                placeholder="Report#"
                value={selectedReportID ?? undefined}
                testId="filter-report-select"
                classNamePrefix="select-dropdown report"
                getPopupContainer={() => document.body}
                dropdownMatchSelectWidth
                disabled={!selectedProject || !selectedRegionId || !selectedCouncilId || (!reports || reports.length === 0)}
              />
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
};

export default FilterModal;
